// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Breadcrumb,
  BreadcrumbItem,
  Tile,
  ClickableTile,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tag,
  Button,
  ProgressBar,
  InlineNotification,
  Modal,
  TextArea,
  Select,
  SelectItem,
  Stack,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
} from '@carbon/react';
import {
  CheckmarkFilled,
  WarningFilled,
  ErrorFilled,
  Security,
  Document,
  Analytics,
  Report,
  Time,
  UserAccess,
  DataBackup,
  Policy,
} from '@carbon/icons-react';
import { Link } from 'react-router-dom';
import { AuthGuard } from '../../components/auth/AuthGuard';
import { auditService, ComplianceReport, ComplianceViolation } from '../../services/auditService';
import { privacyService } from '../../services/privacyService';

interface ComplianceMetric {
  regulation: string;
  score: number;
  status: 'compliant' | 'warning' | 'violation';
  issues: number;
  lastAudit: string;
  nextAudit: string;
}

interface DataRetentionStat {
  dataType: string;
  totalRecords: number;
  expiredRecords: number;
  pendingDeletion: number;
  retentionPeriod: string;
}

export const ComplianceDashboardPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [complianceMetrics, setComplianceMetrics] = useState<ComplianceMetric[]>([]);
  const [violations, setViolations] = useState<ComplianceViolation[]>([]);
  const [retentionStats, setRetentionStats] = useState<DataRetentionStat[]>([]);
  const [complianceReport, setComplianceReport] = useState<ComplianceReport | null>(null);
  const [showViolationModal, setShowViolationModal] = useState(false);
  const [selectedViolation, setSelectedViolation] = useState<ComplianceViolation | null>(null);
  const [remediationText, setRemediationText] = useState('');
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    subtitle?: string;
  } | null>(null);

  useEffect(() => {
    loadComplianceData();
  }, []);

  const loadComplianceData = async () => {
    setLoading(true);
    try {
      const [metricsData, violationsData, retentionData, reportData] = await Promise.all([
        loadComplianceMetrics(),
        auditService.getComplianceViolations({ resolved: false }),
        loadRetentionStats(),
        auditService.generateComplianceReport(
          'all',
          new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          new Date().toISOString()
        ),
      ]);

      setComplianceMetrics(metricsData);
      setViolations(violationsData);
      setRetentionStats(retentionData);
      setComplianceReport(reportData);
    } catch (error) {
      console.error('Failed to load compliance data:', error);
      setNotification({
        type: 'error',
        title: 'Failed to load compliance data',
        subtitle: 'Please refresh the page to try again',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadComplianceMetrics = async (): Promise<ComplianceMetric[]> => {
    // Mock data - in production, this would come from the API
    return [
      {
        regulation: 'GDPR',
        score: 95,
        status: 'compliant',
        issues: 2,
        lastAudit: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        nextAudit: new Date(Date.now() + 23 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        regulation: 'COPPA',
        score: 98,
        status: 'compliant',
        issues: 1,
        lastAudit: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
        nextAudit: new Date(Date.now() + 16 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        regulation: 'FERPA',
        score: 92,
        status: 'compliant',
        issues: 3,
        lastAudit: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
        nextAudit: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        regulation: 'CCPA',
        score: 88,
        status: 'warning',
        issues: 5,
        lastAudit: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        nextAudit: new Date(Date.now() + 25 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ];
  };

  const loadRetentionStats = async (): Promise<DataRetentionStat[]> => {
    // Mock data - in production, this would come from the API
    return [
      {
        dataType: 'User Accounts',
        totalRecords: 125000,
        expiredRecords: 2400,
        pendingDeletion: 180,
        retentionPeriod: 'Account duration + 1 year',
      },
      {
        dataType: 'Educational Records',
        totalRecords: 850000,
        expiredRecords: 15000,
        pendingDeletion: 1200,
        retentionPeriod: '5 years (FERPA)',
      },
      {
        dataType: 'Analytics Data',
        totalRecords: 5000000,
        expiredRecords: 250000,
        pendingDeletion: 25000,
        retentionPeriod: '2 years',
      },
      {
        dataType: 'Audit Logs',
        totalRecords: 10000000,
        expiredRecords: 0,
        pendingDeletion: 0,
        retentionPeriod: '3 years (Immutable)',
      },
    ];
  };

  const handleResolveViolation = async () => {
    if (!selectedViolation || !remediationText) return;

    try {
      await auditService.resolveComplianceViolation(selectedViolation.id, remediationText);

      setNotification({
        type: 'success',
        title: 'Violation resolved',
        subtitle: 'The compliance violation has been marked as resolved',
      });

      setShowViolationModal(false);
      setSelectedViolation(null);
      setRemediationText('');
      await loadComplianceData();
    } catch (error) {
      console.error('Failed to resolve violation:', error);
      setNotification({
        type: 'error',
        title: 'Failed to resolve violation',
        subtitle: 'Please try again',
      });
    }
  };

  const generateComplianceReport = async (regulation: string) => {
    try {
      const report = await auditService.generateComplianceReport(
        regulation as any,
        new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
        new Date().toISOString()
      );

      // Convert to PDF and download
      const blob = new Blob([JSON.stringify(report, null, 2)], {
        type: 'application/json',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `compliance-report-${regulation}-${new Date().toISOString()}.json`;
      a.click();
      window.URL.revokeObjectURL(url);

      setNotification({
        type: 'success',
        title: 'Report generated',
        subtitle: `${regulation} compliance report has been downloaded`,
      });
    } catch (error) {
      console.error('Failed to generate report:', error);
      setNotification({
        type: 'error',
        title: 'Failed to generate report',
        subtitle: 'Please try again',
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'compliant':
        return <CheckmarkFilled size={24} />;
      case 'warning':
        return <WarningFilled size={24} />;
      case 'violation':
        return <ErrorFilled size={24} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant':
        return 'green';
      case 'warning':
        return 'orange';
      case 'violation':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'red';
      case 'high':
        return 'orange';
      case 'medium':
        return 'blue';
      case 'low':
        return 'cyan';
      default:
        return 'gray';
    }
  };

  return (
    <AuthGuard requiredRole="admin">
      <Grid fullWidth>
        <Column lg={16} md={8} sm={4}>
          <Breadcrumb>
            <BreadcrumbItem>
              <Link to="/">Home</Link>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <Link to="/admin">Admin</Link>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>Compliance Dashboard</BreadcrumbItem>
          </Breadcrumb>

          <div className="page-header">
            <h1>Compliance Management Dashboard</h1>
            <p>Monitor GDPR, COPPA, FERPA, and CCPA compliance status</p>
          </div>

          {notification && (
            <InlineNotification
              kind={notification.type}
              title={notification.title}
              subtitle={notification.subtitle}
              onClose={() => setNotification(null)}
              lowContrast
            />
          )}

          {/* Compliance Overview Tiles */}
          <Grid narrow className="compliance-overview">
            {complianceMetrics.map(metric => (
              <Column key={metric.regulation} lg={4} md={4} sm={4}>
                <ClickableTile
                  className={`compliance-tile compliance-tile--${metric.status}`}
                  onClick={() => generateComplianceReport(metric.regulation.toLowerCase())}
                >
                  <div className="compliance-tile__header">
                    {getStatusIcon(metric.status)}
                    <h3>{metric.regulation}</h3>
                  </div>
                  <div className="compliance-tile__score">
                    <span className="score-value">{metric.score}%</span>
                    <span className="score-label">Compliance Score</span>
                  </div>
                  <ProgressBar
                    label=""
                    value={metric.score}
                    max={100}
                    status={metric.status === 'compliant' ? 'finished' : 'active'}
                  />
                  <div className="compliance-tile__details">
                    <p>
                      <strong>Issues:</strong> {metric.issues}
                    </p>
                    <p>
                      <strong>Last Audit:</strong> {new Date(metric.lastAudit).toLocaleDateString()}
                    </p>
                    <p>
                      <strong>Next Audit:</strong> {new Date(metric.nextAudit).toLocaleDateString()}
                    </p>
                  </div>
                  <Tag kind={getStatusColor(metric.status)}>
                    {metric.status.charAt(0).toUpperCase() + metric.status.slice(1)}
                  </Tag>
                </ClickableTile>
              </Column>
            ))}
          </Grid>

          {/* Tabbed Interface */}
          <Tabs selectedIndex={activeTab} onChange={({ selectedIndex }) => setActiveTab(selectedIndex)}>
            <TabList aria-label="Compliance dashboard tabs">
              <Tab renderIcon={WarningFilled}>Violations</Tab>
              <Tab renderIcon={DataBackup}>Data Retention</Tab>
              <Tab renderIcon={Analytics}>Audit Analytics</Tab>
              <Tab renderIcon={Policy}>Policies</Tab>
            </TabList>

            <TabPanels>
              {/* Violations Tab */}
              <TabPanel>
                <Stack gap={5}>
                  <h3>Active Compliance Violations</h3>
                  {violations.length === 0 ? (
                    <Tile>
                      <CheckmarkFilled size={32} style={{ color: 'var(--cds-support-success)' }} />
                      <h4>No active violations</h4>
                      <p>All compliance issues have been resolved</p>
                    </Tile>
                  ) : (
                    <DataTable
                      rows={violations}
                      headers={[
                        { key: 'timestamp', header: 'Date' },
                        { key: 'regulation', header: 'Regulation' },
                        { key: 'severity', header: 'Severity' },
                        { key: 'description', header: 'Description' },
                        { key: 'affectedUsers', header: 'Affected Users' },
                        { key: 'actions', header: 'Actions' },
                      ]}
                    >
                      {({ rows, headers, getTableProps, getHeaderProps, getRowProps }: any) => (
                        <TableContainer>
                          <Table {...getTableProps()}>
                            <TableHead>
                              <TableRow>
                                {headers.map((header: any) => (
                                  <TableHeader {...getHeaderProps({ header })} key={header.key}>
                                    {header.header}
                                  </TableHeader>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {rows.map((row: any) => (
                                <TableRow {...getRowProps({ row })} key={row.id}>
                                  {row.cells.map((cell: any) => {
                                    if (cell.id.includes('timestamp')) {
                                      return (
                                        <TableCell key={cell.id}>{new Date(cell.value).toLocaleDateString()}</TableCell>
                                      );
                                    }
                                    if (cell.id.includes('severity')) {
                                      return (
                                        <TableCell key={cell.id}>
                                          <Tag kind={getSeverityColor(cell.value)}>{cell.value}</Tag>
                                        </TableCell>
                                      );
                                    }
                                    if (cell.id.includes('actions')) {
                                      const violation = violations.find(v => v.id === row.id);
                                      return (
                                        <TableCell key={cell.id}>
                                          <Button
                                            size="sm"
                                            kind="ghost"
                                            onClick={() => {
                                              setSelectedViolation(violation!);
                                              setShowViolationModal(true);
                                            }}
                                          >
                                            Resolve
                                          </Button>
                                        </TableCell>
                                      );
                                    }
                                    return <TableCell key={cell.id}>{cell.value}</TableCell>;
                                  })}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </DataTable>
                  )}
                </Stack>
              </TabPanel>

              {/* Data Retention Tab */}
              <TabPanel>
                <Stack gap={5}>
                  <h3>Data Retention Management</h3>
                  <DataTable
                    rows={retentionStats}
                    headers={[
                      { key: 'dataType', header: 'Data Type' },
                      { key: 'totalRecords', header: 'Total Records' },
                      { key: 'expiredRecords', header: 'Expired' },
                      { key: 'pendingDeletion', header: 'Pending Deletion' },
                      { key: 'retentionPeriod', header: 'Retention Period' },
                    ]}
                  >
                    {({ rows, headers, getTableProps, getHeaderProps, getRowProps }: any) => (
                      <TableContainer>
                        <Table {...getTableProps()}>
                          <TableHead>
                            <TableRow>
                              {headers.map((header: any) => (
                                <TableHeader {...getHeaderProps({ header })} key={header.key}>
                                  {header.header}
                                </TableHeader>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {rows.map((row: any) => (
                              <TableRow {...getRowProps({ row })} key={row.id}>
                                {row.cells.map((cell: any) => {
                                  if (typeof cell.value === 'number' && cell.value > 1000) {
                                    return <TableCell key={cell.id}>{cell.value.toLocaleString()}</TableCell>;
                                  }
                                  return <TableCell key={cell.id}>{cell.value}</TableCell>;
                                })}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </DataTable>

                  <div className="retention-actions">
                    <Button renderIcon={DataBackup}>Run Retention Cleanup</Button>
                    <Button kind="secondary" renderIcon={Document}>
                      Export Retention Report
                    </Button>
                  </div>
                </Stack>
              </TabPanel>

              {/* Audit Analytics Tab */}
              <TabPanel>
                <Stack gap={5}>
                  <h3>Compliance Analytics</h3>
                  {complianceReport && (
                    <Grid narrow>
                      <Column lg={8} md={4} sm={4}>
                        <Tile>
                          <h4>GDPR Compliance</h4>
                          <div className="metric-value">{complianceReport.regulations.gdpr.score}%</div>
                          <p>
                            Last audit: {new Date(complianceReport.regulations.gdpr.lastAudit).toLocaleDateString()}
                          </p>
                          {complianceReport.regulations.gdpr.issues.length > 0 && (
                            <div className="issues-list">
                              <h5>Issues:</h5>
                              <ul>
                                {complianceReport.regulations.gdpr.issues.map((issue, idx) => (
                                  <li key={idx}>{issue}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </Tile>
                      </Column>

                      <Column lg={8} md={4} sm={4}>
                        <Tile>
                          <h4>COPPA Compliance</h4>
                          <div className="metric-value">{complianceReport.regulations.coppa.score}%</div>
                          <p>
                            Last audit: {new Date(complianceReport.regulations.coppa.lastAudit).toLocaleDateString()}
                          </p>
                          {complianceReport.regulations.coppa.issues.length > 0 && (
                            <div className="issues-list">
                              <h5>Issues:</h5>
                              <ul>
                                {complianceReport.regulations.coppa.issues.map((issue, idx) => (
                                  <li key={idx}>{issue}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </Tile>
                      </Column>
                    </Grid>
                  )}

                  <div className="analytics-actions">
                    <Button renderIcon={Analytics}>Generate Full Report</Button>
                    <Button kind="secondary" renderIcon={Time}>
                      Schedule Audit
                    </Button>
                  </div>
                </Stack>
              </TabPanel>

              {/* Policies Tab */}
              <TabPanel>
                <Stack gap={5}>
                  <h3>Privacy Policies & Documentation</h3>
                  <Grid narrow>
                    <Column lg={8} md={4} sm={4}>
                      <ClickableTile href="/privacy-policy" target="_blank">
                        <Document size={24} />
                        <h4>Privacy Policy</h4>
                        <p>Last updated: {new Date().toLocaleDateString()}</p>
                        <Tag kind="green">Current</Tag>
                      </ClickableTile>
                    </Column>

                    <Column lg={8} md={4} sm={4}>
                      <ClickableTile href="/terms-of-service" target="_blank">
                        <Policy size={24} />
                        <h4>Terms of Service</h4>
                        <p>Last updated: {new Date().toLocaleDateString()}</p>
                        <Tag kind="green">Current</Tag>
                      </ClickableTile>
                    </Column>

                    <Column lg={8} md={4} sm={4}>
                      <ClickableTile href="/cookie-policy" target="_blank">
                        <Security size={24} />
                        <h4>Cookie Policy</h4>
                        <p>Last updated: {new Date().toLocaleDateString()}</p>
                        <Tag kind="green">Current</Tag>
                      </ClickableTile>
                    </Column>

                    <Column lg={8} md={4} sm={4}>
                      <ClickableTile href="/data-retention-policy" target="_blank">
                        <DataBackup size={24} />
                        <h4>Data Retention Policy</h4>
                        <p>Last updated: {new Date().toLocaleDateString()}</p>
                        <Tag kind="green">Current</Tag>
                      </ClickableTile>
                    </Column>
                  </Grid>

                  <div className="policy-actions">
                    <Button renderIcon={Document}>Update Policies</Button>
                    <Button kind="secondary" renderIcon={UserAccess}>
                      Review Access Logs
                    </Button>
                  </div>
                </Stack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Column>
      </Grid>

      {/* Resolve Violation Modal */}
      <Modal
        open={showViolationModal}
        modalHeading="Resolve Compliance Violation"
        modalLabel="Compliance Management"
        primaryButtonText="Resolve"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleResolveViolation}
        onRequestClose={() => {
          setShowViolationModal(false);
          setSelectedViolation(null);
          setRemediationText('');
        }}
      >
        {selectedViolation && (
          <Stack gap={5}>
            <div>
              <h4>Violation Details</h4>
              <p>
                <strong>Regulation:</strong> {selectedViolation.regulation}
              </p>
              <p>
                <strong>Severity:</strong>{' '}
                <Tag kind={getSeverityColor(selectedViolation.severity)}>{selectedViolation.severity}</Tag>
              </p>
              <p>
                <strong>Description:</strong> {selectedViolation.description}
              </p>
              <p>
                <strong>Affected Users:</strong> {selectedViolation.affectedUsers}
              </p>
              <p>
                <strong>Date:</strong> {new Date(selectedViolation.timestamp).toLocaleString()}
              </p>
            </div>

            <TextArea
              id="remediation-text"
              labelText="Remediation Actions Taken"
              placeholder="Describe the actions taken to resolve this violation..."
              value={remediationText}
              onChange={e => setRemediationText(e.target.value)}
              rows={5}
              required
            />

            <InlineNotification
              kind="warning"
              title="Important"
              subtitle="Resolving this violation will update the compliance status and notify relevant stakeholders"
              hideCloseButton
              lowContrast
            />
          </Stack>
        )}
      </Modal>
    </AuthGuard>
  );
};
